import React, { useState, useEffect } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import "../../index.css";
// Use local video for presentation
import videoSrc from "../../assets/ele.mp4";
import cmsApi from "../../services/cmsApi";
import { getImageUrl } from "../../utils/getImageUrl";
// Commented out image imports for now - will be used later
// import hero1 from "../../assets/hero-img-1.jpg";
// import hero2 from "../../assets/hero-img-2.jpg";
// import hero3 from "../../assets/hero-img-3.jpg";
// import hero4 from "../../assets/hero-img-4.jpg";
// import hero5 from "../../assets/hero-img-5.jpg";

// Commented out images array for now - will be used later
// const images = [hero1, hero2, hero3, hero4, hero5];

const HeroSection = () => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [heroContent, setHeroContent] = useState({
    title: "GET READY",
    subtitle: "UNLEASH YOUR GREATNESS",
    videoUrl: videoSrc,
    imageUrl: '',
    imageAlt: '',
    mediaType: 'video',
    ctaText: "EXPLORE COURSES",
    ctaLink: "#programs",
    cta2Text: "PRICING & SCHOLARSHIPS",
    cta2Link: "/fees",
    cta3Text: "WHY JRIIT",
    cta3Link: "#about"
  });

  // Fetch hero content from CMS (client-side only)
  useEffect(() => {
    // Only fetch on client side to avoid hydration issues
    if (typeof window === 'undefined') return;
    
    const fetchHeroContent = async () => {
      try {
        // Fetch hero media (image/video) from new endpoint
        const mediaResult = await cmsApi.getHeroMedia();
        
        // Fetch hero component data for text content
        const componentResult = await cmsApi.getHomepageComponentByType('hero');
        
        let mediaType = 'video';
        let imageUrl = '';
        let imageAlt = '';
        let videoUrl = videoSrc;
        
        // Check if mediaResult has success field or if it's the data directly
        if (mediaResult && !mediaResult.error) {
          // API returns data directly on success, or with success: true
          mediaType = mediaResult.mediaType || 'video';
          imageUrl = mediaResult.imageUrl || '';
          imageAlt = mediaResult.imageAlt || '';
          videoUrl = mediaResult.videoUrl || videoSrc;
          
          // Convert relative image paths to full URLs (for CMS uploads)
          // External URLs (http/https) are used as-is
          if (imageUrl && imageUrl.startsWith('/uploads/')) {
            imageUrl = getImageUrl(imageUrl);
          } else if (imageUrl && !imageUrl.startsWith('http://') && !imageUrl.startsWith('https://') && !imageUrl.startsWith('/')) {
            // If it's not a full URL, relative path, or upload path, it might be invalid
          }
          
          // Validate video URL - must be a direct video file or embeddable URL
          if (videoUrl && videoUrl !== videoSrc) {
            const isDirectVideo = videoUrl.includes('.mp4') || videoUrl.includes('.webm') || videoUrl.includes('.ogg') || videoUrl.includes('.mov') || videoUrl.includes('.avi');
            const isEmbeddable = videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be') || videoUrl.includes('vimeo.com');
            const isPexelsPage = videoUrl.includes('pexels.com/video/') || videoUrl.includes('pexels.com/download');
            const isFreepikPage = videoUrl.includes('freepik.com/free-video/') || videoUrl.includes('freepik.com/download');
            const isPageUrl = videoUrl.includes('/video/') || videoUrl.includes('/download') || videoUrl.includes('#fromView');
            
            if (!isDirectVideo && !isEmbeddable) {
              if (isPexelsPage || isFreepikPage || isPageUrl) {
                // Fall back to default video for invalid page URLs
                videoUrl = videoSrc;
              } else {
                // For safety, fall back to default if we can't determine it's valid
                videoUrl = videoSrc;
              }
            }
          }
          
          // Convert relative video paths to full URLs (for CMS uploads)
          if (videoUrl && videoUrl.startsWith('/uploads/')) {
            const CMS_API_URL = process.env.REACT_APP_CMS_API_URL || 'https://jriit.beetletz.online';
            const baseUrl = CMS_API_URL.replace('/api/public', '');
            videoUrl = `${baseUrl}${videoUrl}`;
          }
          
        } else {
          // Hero media fetch failed or returned error
        }
        
        // Update hero content with both media and component data
        if (componentResult.success && componentResult.components && componentResult.components.length > 0) {
          const hero = componentResult.components[0];
          
          // Ensure imageUrl is converted to full URL if it's a relative path
          // External URLs (http/https) are used as-is
          let finalImageUrl = imageUrl;
          if (imageUrl) {
            if (imageUrl.startsWith('/uploads/')) {
              finalImageUrl = getImageUrl(imageUrl);
            } else if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
              // External URL - use as-is
              finalImageUrl = imageUrl;
            } else {
              // Might be a relative path or invalid - try to use as-is
              finalImageUrl = imageUrl;
            }
          }
          
          // Final validation of video URL before setting content
          let finalVideoUrl = videoUrl;
          if (finalVideoUrl && finalVideoUrl !== videoSrc) {
            const isDirectVideo = finalVideoUrl.includes('.mp4') || finalVideoUrl.includes('.webm') || finalVideoUrl.includes('.ogg') || finalVideoUrl.includes('.mov') || finalVideoUrl.includes('.avi');
            const isEmbeddable = finalVideoUrl.includes('youtube.com') || finalVideoUrl.includes('youtu.be') || finalVideoUrl.includes('vimeo.com');
            const isPageUrl = finalVideoUrl.includes('pexels.com/video/') || finalVideoUrl.includes('pexels.com/download') || 
                             finalVideoUrl.includes('freepik.com/free-video/') || finalVideoUrl.includes('freepik.com/download') ||
                             (finalVideoUrl.includes('/video/') && !isDirectVideo) || finalVideoUrl.includes('#fromView');
            
            if (!isDirectVideo && !isEmbeddable && isPageUrl) {
              finalVideoUrl = videoSrc;
            }
          }
          
          const updatedContent = {
            title: hero.title || "GET READY",
            subtitle: hero.subtitle || "UNLEASH YOUR GREATNESS",
            videoUrl: finalVideoUrl,
            imageUrl: finalImageUrl,
            imageAlt: imageAlt,
            mediaType: mediaType,
            ctaText: hero.ctaText || "EXPLORE COURSES",
            ctaLink: hero.ctaLink || "#programs",
            cta2Text: hero.cta2Text || "PRICING & SCHOLARSHIPS",
            cta2Link: hero.cta2Link || "/fees",
            cta3Text: hero.cta3Text || "WHY JRIIT",
            cta3Link: hero.cta3Link || "#about"
          };
          
          setHeroContent(updatedContent);
        } else {
          // Use media data even if component data is not available
          // Ensure imageUrl is converted to full URL if it's a relative path
          let finalImageUrl = imageUrl;
          if (imageUrl) {
            if (imageUrl.startsWith('/uploads/')) {
              finalImageUrl = getImageUrl(imageUrl);
            } else if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
              finalImageUrl = imageUrl;
            }
          }
          
          // Final validation of video URL
          let finalVideoUrl = videoUrl;
          if (finalVideoUrl && finalVideoUrl !== videoSrc) {
            const isDirectVideo = finalVideoUrl.includes('.mp4') || finalVideoUrl.includes('.webm') || finalVideoUrl.includes('.ogg') || finalVideoUrl.includes('.mov') || finalVideoUrl.includes('.avi');
            const isEmbeddable = finalVideoUrl.includes('youtube.com') || finalVideoUrl.includes('youtu.be') || finalVideoUrl.includes('vimeo.com');
            const isPageUrl = finalVideoUrl.includes('pexels.com/video/') || finalVideoUrl.includes('pexels.com/download') || 
                             finalVideoUrl.includes('freepik.com/free-video/') || finalVideoUrl.includes('freepik.com/download') ||
                             (finalVideoUrl.includes('/video/') && !isDirectVideo) || finalVideoUrl.includes('#fromView');
            
            if (!isDirectVideo && !isEmbeddable && isPageUrl) {
              finalVideoUrl = videoSrc;
            }
          }
          
          const updatedContent = {
            ...heroContent,
            videoUrl: finalVideoUrl,
            imageUrl: finalImageUrl,
            imageAlt: imageAlt,
            mediaType: mediaType
          };
          setHeroContent(updatedContent);
        }
      } catch {
        // Use default hero content on error
      }
    };

    fetchHeroContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Commented out image state and effect for now - will be used later
  // const [currentImage, setCurrentImage] = useState(0);

  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     setCurrentImage((prevImage) => (prevImage + 1) % images.length);
  //   }, 10000);

  //   return () => clearInterval(interval);
  // }, []);

  return (
    <div className="relative h-[80vh] overflow-hidden">
      {/* Background Media - Image or Video */}
      <div className="absolute inset-0">
        {heroContent.mediaType === 'image' && heroContent.imageUrl ? (
          // Background Image
          <img
            src={heroContent.imageUrl}
            alt={heroContent.imageAlt || 'Hero background'}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to video if image fails to load
              console.error('❌ Hero image failed to load:', heroContent.imageUrl);
              console.error('This could be due to:');
              console.error('1. CORS restrictions (external site blocking cross-origin requests)');
              console.error('2. Invalid or broken image URL');
              console.error('3. Network connectivity issues');
              console.log('Falling back to video:', heroContent.videoUrl || videoSrc);
              e.currentTarget.style.display = 'none';
              // Switch to video mode if image fails
              setHeroContent(prev => ({
                ...prev,
                mediaType: 'video',
                imageUrl: ''
              }));
            }}
            onLoad={() => {
            }}
          />
        ) : (
          // Background Video
          <video
            key={heroContent.videoUrl} // Force re-render when video URL changes
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay
            loop
            muted
            playsInline
            onError={() => {
              // Fall back to default video if CMS video fails
              if (heroContent.videoUrl !== videoSrc) {
                setHeroContent(prev => ({
                  ...prev,
                  videoUrl: videoSrc
                }));
              }
            }}
          >
            <source src={heroContent.videoUrl || videoSrc} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        )}
      </div>

      {/* Content Overlay */}
      <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col justify-center items-center text-white p-4 min-[1024px]:max-[1535px]:!px-3">
        <h1 className="text-6xl md:text-7xl lg:text-8xl tracking-wider font-bold mb-4 text-center min-[1024px]:max-[1535px]:!text-5xl min-[1024px]:max-[1535px]:!mb-3 min-[1024px]:max-[1535px]:!leading-tight">
          {heroContent.title}
        </h1>
        <p className="text-3xl md:text-4xl lg:text-5xl tracking-wide mt-4 font-semibold mb-12 text-center min-[1024px]:max-[1535px]:!text-2xl min-[1024px]:max-[1535px]:!mt-3 min-[1024px]:max-[1535px]:!mb-8">
          {heroContent.subtitle}
        </p>

        {/* Buttons */}
        <div className="flex flex-wrap justify-center gap-4 min-[1024px]:max-[1535px]:!gap-2">
          {/* CTA 1 Button */}
          {heroContent.ctaText && (
            <CTAButton 
              text={heroContent.ctaText} 
              link={heroContent.ctaLink || '#programs'}
            />
          )}
          
          {/* CTA 2 Button - Dropdown (if link is not provided, show dropdown) */}
          {heroContent.cta2Text && (
            <div 
              className="relative"
              onMouseEnter={() => setIsDropdownOpen(true)}
              onMouseLeave={() => setIsDropdownOpen(false)}
            >
              {heroContent.cta2Link ? (
                <CTAButton 
                  text={heroContent.cta2Text} 
                  link={heroContent.cta2Link}
                />
              ) : (
                <>
                  <button 
                    className="bg-custom-pink hover:bg-gold text-white font-bold py-4 px-8 rounded-full flex items-center transition duration-300 min-[1024px]:max-[1535px]:!py-2.5 min-[1024px]:max-[1535px]:!px-5 min-[1024px]:max-[1535px]:!text-sm"
                    onClick={() => setIsDropdownOpen(prev => !prev)}
                  >
                    {heroContent.cta2Text}
                    <ChevronDown className="ml-2 h-5 w-5" />
                  </button>
                  
                  {/* Dropdown Menu */}
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 w-48 bg-white rounded-lg shadow-lg z-50 border-t-2 border-custom-pink">
                      <Link 
                        to="/fees" 
                        className="block px-4 py-3 text-gray-800 hover:bg-gray-100 transition-colors duration-200"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        Pricing
                      </Link>
                      <a 
                        href="https://giccs.or.tz/donation/application" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="block px-4 py-3 text-gray-800 hover:bg-gray-100 transition-colors duration-200"
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        Scholarship
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          
          {/* CTA 3 Button */}
          {heroContent.cta3Text && (
            <CTAButton 
              text={heroContent.cta3Text} 
              link={heroContent.cta3Link || '#about'}
            />
          )}
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 min-[1024px]:max-[1535px]:!bottom-3">
        <ChevronDown className="w-10 h-10 min-[1024px]:max-[1535px]:!h-8 min-[1024px]:max-[1535px]:!w-8 text-white animate-bounce rotate-90" />
      </div>

    </div>
  );
};

// CTA Button Component that handles both hash links and regular links
const CTAButton = ({ text, link }) => {
  const handleClick = (e) => {
    if (!link) return;
    
    // If it's a hash link (starts with #), scroll to element
    if (link.startsWith('#')) {
      e.preventDefault();
      const elementId = link.substring(1); // Remove the #
      const el = document.getElementById(elementId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // If element not found, try to navigate
        window.location.hash = link;
      }
    } else if (link.startsWith('/')) {
      // Internal route - use React Router Link
      // The Link component will handle this
    } else if (link.startsWith('http://') || link.startsWith('https://')) {
      // External link - open in same window
      window.location.href = link;
    }
  };

  // If it's an internal route (starts with /), use Link component
  if (link && link.startsWith('/') && !link.startsWith('//')) {
    return (
      <Link 
        to={link}
        className="bg-custom-pink hover:bg-gold text-white font-bold py-4 px-8 rounded-full flex items-center transition duration-300 min-[1024px]:max-[1535px]:!py-2.5 min-[1024px]:max-[1535px]:!px-5 min-[1024px]:max-[1535px]:!text-sm"
      >
        {text}
        <ChevronRight className="ml-2 h-5 w-5" />
      </Link>
    );
  }

  // For hash links or external links, use button
  return (
    <button 
      onClick={handleClick}
      className="bg-custom-pink hover:bg-gold text-white font-bold py-4 px-8 rounded-full flex items-center transition duration-300 min-[1024px]:max-[1535px]:!py-2.5 min-[1024px]:max-[1535px]:!px-5 min-[1024px]:max-[1535px]:!text-sm"
    >
      {text}
      <ChevronRight className="ml-2 h-5 w-5" />
    </button>
  );
};

export default HeroSection;

